/* eslint-disable max-lines */
import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react'
import { Title } from '@usap/widgets'
import { LazyLoad, Box, ResponsiveBox } from '@usap/uicomponents'
import RecentlyViewedWmo from './RecentlyViewedWmo'
import { LocalContext } from '../helpers/useLocalContext'
import { useSerpContext } from '../contexts/serp.context'
import getInventoryDetails from '../helpers/getInventoryDetails'
import { GTM } from '../helpers'
import { getSyncSEDetails } from '../helpers/getShippingExpectationDate'
import RecentlyViewCard from './Loadables/RecentlyViewCard.loadable'
import useIsMounted from '../../hooks/useIsMounted'
import useWmoEnabled from '../hooks/useWmoEnabled'
import styled from 'styled-components'
import { usef40flag } from '../hooks/constructor'
import { useFeatureFlags } from '@usap/core'

const MainContainer = styled(ResponsiveBox)`
  padding: 0 12px;
`

const styleCSS = {
  pad: { horizontal: 'small', vertical: 'medium' },
  margin: { horizontal: 'auto', vertical: 'medium' },
  fxstyle: {
    other: { minWidth: '300px', overflowY: 'auto' },
    large: {},
  },
  fontsize: { fontSize: '20px' },
}

export default function RecentlyViewed({ isBrowser }) {
  const { isBot, isDesktop } = useContext(LocalContext)
  const [records, setRecords] = useState([])
  const [shippingExp, setShippingExp] = useState({})
  const isMounted = useIsMounted()

  const serpState = useSerpContext()
  const { recentlyViewed, recentlyViewedBefore } = serpState || {}
  const { productBasicImpressions, getGroundDeliveryTime } = GTM
  const [firstImpression, setFirstImpression] = useState(true)

  const setRecentlyViewed = useCallback(
    currentRecentlyViewedBefore => {
      serpState.setRecentlyViewedBefore(currentRecentlyViewedBefore + 1)
    },
    [serpState]
  )

  const pids = useMemo(() => records.map(item => item.id).join('|'), [records])

  useEffect(() => {
    if (firstImpression && records.length > 0) {
      const recentlyViewedBeforeItem = parseInt(recentlyViewedBefore, 10)

      const newRecentlyViewedBefore =
        (Number.isNaN(recentlyViewedBeforeItem)
          ? 0
          : recentlyViewedBeforeItem) || 0

      if (newRecentlyViewedBefore !== records.length) {
        Promise.all([
          getInventoryDetails(pids, false),
          getSyncSEDetails(records),
        ])
          .then(([invDetails, seData]) => {
            if (!isMounted()) return

            setShippingExp(seData)
            productBasicImpressions(
              'Recently Viewed Products',
              records.map(record => ({
                ...record,
                inventory:
                  records.length > 1 ? invDetails[record.id] : invDetails,
                groundDeliveryTime: getGroundDeliveryTime(seData, record.sku),
              }))
            )
          })
          .catch(() => {
            if (!isMounted()) return
            productBasicImpressions('Recently Viewed Products', records)
          })
          .finally(() => {
            if (!isMounted()) return
            setRecentlyViewed(newRecentlyViewedBefore)
          })
      }

      setFirstImpression(false)
    }
  }, [
    recentlyViewedBefore,
    records,
    firstImpression,
    isMounted,
    setRecentlyViewed,
    getGroundDeliveryTime,
    productBasicImpressions,
    pids,
  ])

  useEffect(() => {
    if (isBrowser) {
      setRecords(recentlyViewed || [])
    }
  }, [isBrowser, recentlyViewed])

  const recentlyViewedHeadingSize = isDesktop ? 'medium' : 'small'

  const recentlyViewedOther = useMemo(
    () =>
      records && records.length ? `${records && records.length * 250}px` : '',
    [records]
  )

  const recentlyViewedFxWidth = useMemo(
    () => ({
      other: recentlyViewedOther,
      large: '100%',
    }),
    [recentlyViewedOther]
  )

  const isWmoEnabled = useWmoEnabled()

  const decisions = useFeatureFlags()
  const isCartRecoEnabled = useMemo(() => usef40flag(decisions), [decisions])
  if (isCartRecoEnabled) return null

  if (!(records && records.length)) return null

  if (isWmoEnabled) {
    return (
      <RecentlyViewedWmo
        recentlyViewedItems={records}
        shippingExp={shippingExp}
      />
    )
  }

  return (
    <LazyLoad once height={400} offset={100} isBot={isBot}>
      <MainContainer
        id="recentlyViewed"
        width="1248px"
        pad={styleCSS.pad}
        margin={styleCSS.margin}
      >
        {records.length !== 0 && (
          <>
            <Box id="recentlyViewedHeading">
              <Title
                type="h2"
                style={styleCSS.fontsize}
                size={recentlyViewedHeadingSize}
                label="Recently Viewed Products"
              />
            </Box>

            <ResponsiveBox fxStyle={styleCSS.fxstyle}>
              <ResponsiveBox
                id="recentlyViewedSection"
                direction="row"
                fxWidth={recentlyViewedFxWidth}
              >
                {records &&
                  records.map((record, index) => (
                    <RecentlyViewCard
                      data={record}
                      getInventoryDetails={getInventoryDetails}
                      key={record.id}
                      index={index}
                      shippingExp={shippingExp}
                    />
                  ))}
              </ResponsiveBox>
            </ResponsiveBox>
          </>
        )}
      </MainContainer>
    </LazyLoad>
  )
}
